import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '../ui/dialog';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Globe, 
  Target, 
  Bug, 
  RefreshCw 
} from 'lucide-react';

/**
 * Helper function to get status icon
 */
const getStatusIcon = (status) => {
  switch (status) {
    case 'PASS':
    case 'passed': return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'FAIL':
    case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
    case 'running': return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
    default: return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

/**
 * Helper function to get status color
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'PASS':
    case 'passed': return 'bg-green-500 text-white';
    case 'FAIL':
    case 'failed': return 'bg-red-500 text-white';
    case 'running': return 'bg-blue-500 text-white';
    default: return 'bg-gray-500 text-white';
  }
};

/**
 * ExecutionDetailsModal - Display API test execution results
 * 
 * Shows detailed information about a test execution including:
 * - Execution metadata (ID, number, timestamp)
 * - Error details for failed tests
 * - Response body with JSON formatting
 * - Assertion results
 * - Jira ticket creation option
 */
export const ExecutionDetailsModal = ({
  // Modal state
  open,
  onOpenChange,
  // Execution data
  execution,
  // Jira integration
  jiraIntegrations = [],
  creatingJiraTicket = false,
  onCreateJiraTicket
}) => {
  const { t, language } = useApiTestingLanguage();
  if (!execution) return null;

  const isFailed = execution.status === 'FAIL' || execution.status === 'failed';
  const failedAssertions = execution.assertion_results?.filter(r => !r.passed) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Test Execution Results")}</DialogTitle>
          <DialogDescription>
            {t("View detailed results from your API test execution.")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Execution ID Information */}
          {execution.id && execution.execution_number && (
            <div className="border-b pb-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-800">{t("Execution Information")}</h4>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{t("Execution Number:")}</span>
                      <Badge variant="outline">#{execution.execution_number}</Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{t("Execution ID:")}</span>
                      <Badge variant="outline" className="font-mono text-xs">
                        {execution.id}
                      </Badge>
                    </div>
                    {execution.started_at && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">{t("Started:")}</span>
                        <span className="text-sm text-gray-800">
                          {(() => {
                            const executedTimeString = execution.started_at;
                            const executedTime = new Date(executedTimeString + (executedTimeString.includes('Z') ? '' : 'Z'));
                            const options = {
                              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            };
                            return executedTime.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', options);
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Information - Show detailed error info for failed tests */}
          {isFailed && (
            <div className="border border-red-200 bg-red-50 p-4 rounded-lg mb-4">
              <div className="flex items-center space-x-2 mb-3">
                <XCircle className="h-5 w-5 text-red-500" />
                <h4 className="font-semibold text-red-800">{t("Test Failure Details")}</h4>
              </div>
              
              {/* Network/Connection Errors */}
              {execution.error_message && (
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Globe className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">{t("Network Error")}</span>
                  </div>
                  <div className="bg-red-100 p-3 rounded border-l-4 border-red-400">
                    <p className="text-sm text-red-800 font-mono">{execution.error_message}</p>
                  </div>
                </div>
              )}
              
              {/* HTTP Status Errors */}
              {!execution.error_message && execution.response_status_code && execution.response_status_code >= 400 && (
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Globe className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">HTTP {execution.response_status_code} {t("Error")}</span>
                  </div>
                  <div className="bg-red-100 p-3 rounded border-l-4 border-red-400">
                    <p className="text-sm text-red-800">
                      {t("Server returned")} {execution.response_status_code} {t("status code")}
                      {execution.response_body && execution.response_body.length < 200 && (
                        <span>: {execution.response_body}</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Assertion Failures Summary */}
              {failedAssertions.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Target className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">
                      {t(failedAssertions.length === 1 ? '{v0} Assertion Failed' : '{v0} Assertions Failed', { v0: failedAssertions.length })}
                    </span>
                  </div>
                  <div className="bg-red-100 p-3 rounded border-l-4 border-red-400">
                    <div className="space-y-1">
                      {failedAssertions.map((result, index) => (
                        <div key={index} className="text-sm text-red-800">
                          • {result.assertion.description || `${result.assertion.type} ${result.assertion.operator} ${result.assertion.expected_value}`}
                          {result.actual_value !== undefined && (
                            <span className="text-red-600"> {t("(got:")} {typeof result.actual_value === 'object' ? JSON.stringify(result.actual_value) : result.actual_value})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Response Time Issues */}
              {execution.response_time && execution.assertion_results && 
               execution.assertion_results.some(r => !r.passed && r.assertion.type === 'response_time') && (
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Clock className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">{t("Response Time Issue")}</span>
                  </div>
                  <div className="bg-red-100 p-3 rounded border-l-4 border-red-400">
                    <p className="text-sm text-red-800">
                      {t("Response took")} {execution.response_time?.toFixed(0)}ms
                    </p>
                  </div>
                </div>
              )}
              
              {/* Generic failure if no specific error info */}
              {!execution.error_message && 
               (!execution.response_status_code || execution.response_status_code < 400) && 
               (!execution.assertion_results || execution.assertion_results.length === 0) && (
                <div className="bg-red-100 p-3 rounded border-l-4 border-red-400">
                  <p className="text-sm text-red-800">{t("Test failed for unknown reasons. Check test configuration and try again.")}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Status Summary */}
          <div className="flex items-center space-x-3">
            {getStatusIcon(execution.status)}
            <span className="text-lg font-semibold">
              {(execution.status === 'PASS' || execution.status === 'passed') ? t("Test Passed") : t("Test Failed")}
            </span>
            <Badge className={getStatusColor(execution.status)}>
              {execution.response_status_code}
            </Badge>
            <span className="text-sm text-gray-600">
              {execution.response_time?.toFixed(0)}ms
            </span>
          </div>

          {/* Response Body */}
          {execution.response_body && (
            <div>
              <h4 className="font-semibold mb-2">{t("Response Body")}</h4>
              <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                <pre className="text-sm">
                  {(() => {
                    try {
                      // Check if response_body is already a string that looks like HTML
                      if (execution.response_body.toString().trim().startsWith('<!DOCTYPE') || 
                          execution.response_body.toString().trim().startsWith('<html')) {
                        return execution.response_body;
                      }
                      // Try to parse as JSON for pretty formatting
                      const parsed = JSON.parse(execution.response_body || '{}');
                      return JSON.stringify(parsed, null, 2);
                    } catch (err) {
                      // If JSON parsing fails, display raw response
                      return execution.response_body || t("No response body");
                    }
                  })()}
                </pre>
              </div>
            </div>
          )}

          {/* Assertion Results */}
          {execution.assertion_results && execution.assertion_results.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">{t("Assertion Results")}</h4>
              <div className="space-y-2">
                {execution.assertion_results.map((result, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                    {result.passed ? 
                      <CheckCircle className="h-4 w-4 text-green-500" /> : 
                      <XCircle className="h-4 w-4 text-red-500" />
                    }
                    <span className="text-sm">
                      {result.assertion.description || `${result.assertion.type} ${result.assertion.operator} ${result.assertion.expected_value}`}
                    </span>
                    {result.actual_value && (
                      <Badge variant="outline" className="text-xs">
                        {t("Actual:")} {typeof result.actual_value === 'object' ? 'JSON' : result.actual_value}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer with Jira Ticket Button */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t mt-6">
            {/* Create Jira Ticket Button - Show only for failed tests */}
            {(isFailed || execution.error_message) && (
              <Button
                onClick={() => onCreateJiraTicket && onCreateJiraTicket(execution)}
                disabled={creatingJiraTicket || jiraIntegrations.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                data-testid="create-jira-ticket-btn"
              >
                {creatingJiraTicket ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t("Creating Ticket...")}
                  </>
                ) : (
                  <>
                    <Bug className="h-4 w-4 mr-2" />
                    {t("Create Jira Ticket")}
                  </>
                )}
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="flex-1 sm:flex-initial"
                data-testid="close-execution-details-btn"
              >
                {t("Close")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionDetailsModal;
